import { useMember } from "@erp/shared/components/ui/Dialog/MemberSearchDialog/useMember";
import { defineStore, storeToRefs } from "pinia";
import { ORDER_BASIC_INFO, orderMemberInfo, orderRequestInfoInputs, } from "./components/BasicInfoWrite.const";
import { computed, ref } from "vue";
import { Immer } from "immer";
import { useDirectInputCost } from "@erp/shared/components/ui/Document/CalcCost/DirectInputCostTable/useDirectInputCost";
import { convertToFileObj } from "@erp/shared/utils/file/convert-to-file-obj";
import { useOrderConfirmAction } from "@erp/shared/views/(order,purchase)/_composables/useOrderConfirmAction";
import { usePurchaseIsCommercialStore } from "./is-commercial.store";
import { useLoginInfo } from "@erp/shared/composables/useLoginInfo";
import { getCustomerCompanies, postCustomerCompany, } from "@erp/shared/api/generated/wrappers/customer";
import { showAlert, showResult } from "@erp/shared/utils/dialog";
import { getObtainConfirmDetail } from "@erp/shared/api/generated/wrappers/obtain";
export const useOrderWriteModalStore = defineStore("orderWriteModal", () => {
    const immer = new Immer({ autoFreeze: false });
    const isEditing = ref(false);
    const isOpen = ref(false);
    const formValidate = ref(false);
    const initialInputs = immer.produce(orderRequestInfoInputs, (draft) => draft);
    const inputs = ref(initialInputs);
    const currentFormData = computed(() => {
        return inputs.value.reduce((acc, item) => {
            const key = item.column_name;
            acc[key] = item.value ?? null;
            return acc;
        }, {});
    });
    const clickedPurchaseProduct = ref(null);
    const currentPurchaseIds = computed(() => {
        return Array.isArray(clickedPurchaseProduct.value)
            ? clickedPurchaseProduct.value.map((item) => item.id)
            : clickedPurchaseProduct.value?.id
                ? [clickedPurchaseProduct.value.id]
                : [];
    });
    // ---------------- 구매 컨텍스트 (optional) ----------------
    const purchaseContext = ref(null);
    // ---------------- 거래처 정보 ----------------
    const selectedCompanyId = ref(null);
    const companyName = ref("");
    // ---------------- 세금포함 ----------------
    const isTaxIncluded = ref(0);
    // ---------------- 품의서 연결 ----------------
    const approvalDocumentId = ref(null);
    const approvalDocEstimateFiles = ref([]);
    // ---------------- 프로젝트 선택 ----------------
    const selectedProjectId = ref(null);
    // ---------------- 컴포저블 ----------------
    const { userData } = useLoginInfo();
    const { memberInfoRef, setMember, checkMemberInfo, selectMemberDialog, applyMemberInfo, setSpecificMember, } = useMember(orderMemberInfo, {}, userData);
    const { calcCostDirectInputData, applyDirectInputCost: applyTable, resetTable, getMenuItems, setDirectInputValidate, rowActions, } = useDirectInputCost();
    // ---------------- 컴포저블(API 요청) ----------------
    const isCommercialStore = usePurchaseIsCommercialStore();
    const { isCommercial } = storeToRefs(isCommercialStore);
    const { orderId, setOrderId, readQuery: { data: currentOrder, isLoading: readOrderLoading, refetch, isSuccess, }, createMutation, updateMutation, } = useOrderConfirmAction(isCommercial);
    // ---------------- 거래처 정보 적용 ----------------
    const applyCompanyInfo = (company) => {
        selectedCompanyId.value = company.id;
        companyName.value = company.name;
        const fieldMapping = {
            [ORDER_BASIC_INFO.대표자]: company.representative ?? null,
            [ORDER_BASIC_INFO.등록번호]: company.registrationNumber ?? null,
            [ORDER_BASIC_INFO.주소]: company.address ?? null,
            [ORDER_BASIC_INFO.전화]: company.phone ?? null,
            [ORDER_BASIC_INFO.팩스]: company.fax ?? null,
            [ORDER_BASIC_INFO.결제조건]: company.paymentTerms ?? null,
            [ORDER_BASIC_INFO.계좌정보]: company.accountNumber ?? null,
        };
        inputs.value = immer.produce(inputs.value, (draft) => {
            draft.forEach((item) => {
                if (item.type === "file")
                    return;
                const mappedValue = fieldMapping[item.column_name];
                if (mappedValue !== undefined) {
                    item.value = mappedValue ?? "";
                }
            });
        });
    };
    const applyCompanyName = (name) => {
        selectedCompanyId.value = null;
        companyName.value = name;
    };
    const isRegisteringCompany = ref(false);
    const registerCompany = async () => {
        if (isRegisteringCompany.value)
            return;
        const name = companyName.value;
        if (!name)
            return;
        isRegisteringCompany.value = true;
        try {
            const existing = await getCustomerCompanies(name);
            if (existing.some((c) => c.name === name)) {
                showAlert("같은 이름의 거래처가 이미 등록되어 있습니다.");
                return;
            }
            const created = await postCustomerCompany({ name });
            selectedCompanyId.value = created.id;
            showResult("거래처가 등록되었습니다.", "완료", false, "확인", "닫기");
        }
        catch {
            showAlert("거래처 등록에 실패했습니다.");
        }
        finally {
            isRegisteringCompany.value = false;
        }
    };
    // ---------------- 구매 품목 → 발주 자재 pre-fill ----------------
    const prefillMaterialsFromPurchase = () => {
        const purchaseProducts = purchaseContext.value?.purchaseProducts;
        if (!purchaseProducts?.length)
            return;
        // 선택된 ID만 필터링
        const selectedIds = currentPurchaseIds.value;
        const selectedProducts = purchaseProducts.filter((p) => selectedIds.includes(p.id));
        if (!selectedProducts.length)
            return;
        // PurchaseProduct → CalcCostDetailData 변환
        const calcData = selectedProducts.map((p) => ({
            cost_list: p.name ?? "",
            product_code: p.productCode ?? "",
            cost_list_sub: p.spec ?? "",
            cost_unit: "EA",
            cost_num: p.purchaseNum ?? 0,
            cost_unit_price: p.unitPrice ?? 0,
            cost_total: (p.purchaseNum ?? 0) * (p.unitPrice ?? 0),
            belong_data: [],
        }));
        applyTable(calcData);
    };
    // ---------------- 납기일 pre-fill (수주서 → 발주서) ----------------
    const prefillDueDateFromObtain = async () => {
        const obtainId = purchaseContext.value?.obtainConfirmationId;
        if (!obtainId)
            return;
        try {
            const obtainData = await getObtainConfirmDetail(obtainId);
            if (obtainData?.info?.dueDate) {
                inputs.value = immer.produce(inputs.value, (draft) => {
                    const dueDateInput = draft.find((item) => item.column_name === ORDER_BASIC_INFO.납기일);
                    if (dueDateInput) {
                        dueDateInput.value = String(obtainData.info.dueDate).split("T")[0];
                    }
                });
            }
        }
        catch {
            // 수주서 조회 실패 시 무시 (사용자가 수동 입력)
        }
    };
    // ---------------- Open / Close / Clear ----------------
    const clearOrder = () => {
        resetTable();
        selectedCompanyId.value = null;
        companyName.value = "";
        isTaxIncluded.value = 0;
        approvalDocumentId.value = null;
        approvalDocEstimateFiles.value = [];
        selectedProjectId.value = null;
        inputs.value = immer.produce(initialInputs, (draft) => {
            draft.forEach((item) => {
                item.value = item.type === "file" ? null : "";
            });
        });
    };
    const openModal = () => {
        isOpen.value = true;
    };
    const closeModal = () => {
        isOpen.value = false;
        clearOrder();
        purchaseContext.value = null;
    };
    /**
     * @description 독립 발주서 작성 모달 열기 (OrderSearchPage에서 호출)
     */
    const openStandaloneOrder = () => {
        isEditing.value = false;
        clickedPurchaseProduct.value = null;
        purchaseContext.value = null;
        setOrderId(null);
        clearOrder();
        openModal();
    };
    /**
     * @description 품의서 연결 발주서 작성 모달 열기 (OrderSearchPage에서 품의서 선택 후 호출)
     * @param doc 선택된 품의서 데이터
     */
    const openOrderFromApprovalDoc = async (doc) => {
        isEditing.value = false;
        clickedPurchaseProduct.value = null;
        purchaseContext.value = null;
        setOrderId(null);
        clearOrder();
        // clearOrder()가 approvalDocumentId를 null로 초기화하므로 반드시 이후에 설정
        approvalDocumentId.value = doc.id;
        approvalDocEstimateFiles.value = doc.estimateFiles ?? [];
        // 품의서의 프로젝트 정보 자동 채움
        selectedProjectId.value = doc.projectId ?? null;
        // 품의서 품목 → 발주 자재 pre-fill (동기, 모달 열기 전)
        if (doc.items?.length) {
            const calcData = doc.items.map((item) => ({
                cost_list: item.itemName ?? "",
                product_code: item.productCode ?? "",
                cost_list_sub: item.spec ?? "",
                cost_unit: "EA",
                cost_num: item.quantity ?? 0,
                cost_unit_price: item.unitPrice ?? 0,
                cost_total: item.total ?? 0,
                belong_data: [],
            }));
            applyTable(calcData);
        }
        // 모달 먼저 열기 (거래처 API 응답 대기 없이 즉시)
        openModal();
        // 거래처 정보는 모달이 열린 후 비동기로 채움
        if (doc.companyName) {
            // 품의서 데이터로 먼저 채우기 (즉시)
            applyCompanyInfo({
                id: doc.companyId ?? null,
                name: doc.companyName,
                representative: null,
                registrationNumber: doc.companyRegistrationNumber ?? null,
                address: null,
                phone: doc.companyPhone ?? null,
                fax: doc.companyFax ?? null,
                paymentTerms: doc.paymentTerms ?? null,
                accountNumber: doc.companyBankInfo ?? null,
            });
            // 거래처 마스터 데이터로 빈 필드 보충 (비동기)
            try {
                const companies = await getCustomerCompanies(doc.companyName);
                let matchedCompany = null;
                if (doc.companyId) {
                    matchedCompany =
                        companies.find((c) => c.id === doc.companyId) ?? null;
                }
                if (!matchedCompany) {
                    matchedCompany =
                        companies.find((c) => c.name === doc.companyName) ?? null;
                }
                if (matchedCompany) {
                    // 현재 폼 값을 읽어서 빈 필드만 보충
                    applyCompanyInfo({
                        id: matchedCompany.id,
                        name: doc.companyName,
                        representative: matchedCompany.representative ?? null,
                        registrationNumber: doc.companyRegistrationNumber ??
                            matchedCompany.registrationNumber ??
                            null,
                        address: matchedCompany.address ?? null,
                        phone: doc.companyPhone ?? matchedCompany.phone ?? null,
                        fax: doc.companyFax ?? matchedCompany.fax ?? null,
                        paymentTerms: doc.paymentTerms ?? matchedCompany.paymentTerms ?? null,
                        accountNumber: doc.companyBankInfo ?? matchedCompany.accountNumber ?? null,
                    });
                }
            }
            catch {
                // 조회 실패 시 품의서 데이터만 유지 (이미 적용됨)
            }
        }
    };
    /**
     * @description 새로운 발주서 작성 모달 열기 (구매 컨텍스트 포함)
     * @param data 선택된 구매 상품 데이터
     * @param context 구매 컨텍스트 (프로젝트, 구매담당자 등)
     */
    const openNewOrder = async (data, context) => {
        isEditing.value = false;
        clickedPurchaseProduct.value = data;
        if (context) {
            purchaseContext.value = context;
            selectedProjectId.value = context.projectId ?? null;
        }
        setOrderId(null);
        // 구매 품목 데이터로 발주 자재 pre-fill
        prefillMaterialsFromPurchase();
        // 수주서 납기일 pre-fill
        await prefillDueDateFromObtain();
        openModal();
    };
    const openExistiongOrder = async (data) => {
        isEditing.value = true;
        clickedPurchaseProduct.value = data;
        const orderId = Array.isArray(data) ? data[0].orderId : data.orderId;
        setOrderId(orderId);
        await refetch();
        await applyOrder();
        openModal();
    };
    // ---------------- 불러온 데이터 적용 ----------------
    const applyForm = () => {
        // 수신업체는 inputs에 없으므로 별도로 적용
        const orderCompanyName = currentOrder.value?.[ORDER_BASIC_INFO.수신업체];
        companyName.value =
            orderCompanyName != null ? String(orderCompanyName) : "";
        // 세금포함 복원
        isTaxIncluded.value = currentOrder.value?.isTaxIncluded ?? 0;
        // 프로젝트 ID 복원
        selectedProjectId.value = currentOrder.value?.projectId ?? null;
        inputs.value = immer.produce(inputs.value, (draft) => {
            draft.forEach((item) => {
                if (item.type === "file")
                    return;
                const orderValue = currentOrder.value[item.column_name];
                if (item.type === "dateSingle") {
                    item.value = String(orderValue).split("T")[0];
                }
                else if (item.type === "dateRange") {
                    item.value = Array.isArray(orderValue)
                        ? orderValue.map((item) => typeof item === "string" ? item : String(item))
                        : [];
                }
                else {
                    item.value = orderValue != null ? String(orderValue) : "";
                }
            });
        });
    };
    const applyFile = async () => {
        const fileFields = inputs.value
            .filter((item) => item.type === "file")
            .map((item) => item.column_name);
        const fileUpdates = [];
        // 파일 로드 작업을 병렬로 처리
        await Promise.allSettled(fileFields.map(async (field) => {
            const fileName = currentOrder.value[field];
            if (fileName) {
                try {
                    const fileObj = await convertToFileObj(fileName);
                    if (fileObj) {
                        fileUpdates.push({ columnName: field, file: fileObj });
                    }
                }
                catch (error) {
                    console.error(`파일 로드 실패 (${field}):`, error);
                }
            }
        }));
        // 한 번에 모든 파일 업데이트 적용
        if (fileUpdates.length > 0) {
            inputs.value = immer.produce(inputs.value, (draft) => {
                fileUpdates.forEach(({ columnName, file }) => {
                    const inputItem = draft.find((item) => item.column_name === columnName);
                    if (inputItem) {
                        inputItem.value = file;
                    }
                });
            });
        }
    };
    const applyOrder = async () => {
        if (!currentOrder.value)
            return;
        if (isSuccess.value) {
            applyForm();
            await applyFile();
            applyMemberInfo(currentOrder.value);
            applyTable(currentOrder.value.productCalcJson ?? []);
        }
    };
    return {
        inputs,
        formValidate,
        currentFormData,
        orderId,
        clickedPurchaseProduct,
        memberInfoRef,
        readOrderLoading,
        setMember,
        checkMemberInfo,
        selectMemberDialog,
        calcCostDirectInputData,
        isOpen,
        currentPurchaseIds,
        isEditing,
        currentOrder,
        selectedCompanyId,
        companyName,
        isTaxIncluded,
        approvalDocumentId,
        approvalDocEstimateFiles,
        isRegisteringCompany,
        purchaseContext,
        selectedProjectId,
        openStandaloneOrder,
        openOrderFromApprovalDoc,
        openNewOrder,
        openExistiongOrder,
        clearOrder,
        openModal,
        closeModal,
        getMenuItems,
        rowActions,
        setDirectInputValidate,
        setSpecificMember,
        createMutation,
        updateMutation,
        applyCompanyInfo,
        applyCompanyName,
        registerCompany,
    };
});
